import React, { useState, useEffect, useRef } from 'react';
import CookieBlockedMessage, { detectContentType } from '../../CommonComponents/CookieBlockedMessage';
import SafeIFrame from '../../CommonComponents/SafeIFrame';
import YoutubeIframe from '../../CommonComponents/YoutubeIframe';
import VimeoIframe from '../../CommonComponents/VimeoIframe';
import { isExternalUrl, shouldShowEmbeddedContent } from '../../../utils/cookieConsentUtils';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
import { gateThirdPartyHtml } from '../../../utils/gateThirdPartyHtml';
import { loadGoogleFontsInHtml } from '../../../utils/googleFontLoader';
import '../../CommonComponents/HtmlText.css';

// Component to handle script-based embeds (ThriveCart, Calendly, etc.)
// Uses iframe isolation so each embed has its own JavaScript context
const ScriptEmbed = ({ htmlContent, height, width }) => {
    const iframeRef = useRef(null);

    useEffect(() => {
        if (!iframeRef.current || !htmlContent) return;

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        // Create complete HTML document for iframe
        const fullHTML = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body {
            margin: 0;
            padding: 0;
            overflow: auto;
        }
    </style>
</head>
<body>
    ${htmlContent}
</body>
</html>
        `;

        // Write HTML to iframe
        iframeDoc.open();
        iframeDoc.write(fullHTML);
        iframeDoc.close();

    }, [htmlContent]);

    return (
        <iframe
            ref={iframeRef}
            style={{
                width: `${width}px`,
                height: `${height}px`,
                border: 'none',
                overflow: 'auto'
            }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            title="Embed Content"
        />
    );
};

const UNEDITED_LIGHTBOX_SIZE = 300;

const Element = ({ elements, dimensions, lightbox = false, videoMonitor = null }) => {
    // True when the KDF is rendered inside a 3rd-party iframe (embed) — matches the
    // app's convention used elsewhere (e.g. the "insideIframe" class in EditKdfPage).
    const isEmbed = typeof window !== 'undefined' && window.self !== window.top;
    // State to force re-render when cookie consent changes
    const [, setCookieStatusChanged] = useState(0);

    // Listen for cookie consent changes to trigger re-render
    useEffect(() => {
        const handleCookieConsentChange = () => {
            setCookieStatusChanged(prev => prev + 1);
        };

        if (typeof window !== 'undefined') {
            window.addEventListener('cookieConsentChanged', handleCookieConsentChange);
            return () => {
                window.removeEventListener('cookieConsentChanged', handleCookieConsentChange);
            };
        }
    }, []);

    useEffect(() => {
        Object.values(elements || {}).forEach((element) => {
            if (element?.element_type_name?.toLowerCase() === 'html text') loadGoogleFontsInHtml(element.value?.html_text);
        });
    }, [elements]);

    const lightboxSize = lightbox && Number(dimensions?.width) > 0 && Number(dimensions?.height) > 0
        ? { width: Number(dimensions.width), height: Number(dimensions.height) }
        : null;
    const isUneditedFrameSize = lightboxSize?.width === UNEDITED_LIGHTBOX_SIZE && lightboxSize?.height === UNEDITED_LIGHTBOX_SIZE;
    const frameStyle = lightboxSize && !isUneditedFrameSize
        ? { width: `min(${lightboxSize.width}px, ${isEmbed ? '80vw' : '85vw'})`, aspectRatio: `${lightboxSize.width} / ${lightboxSize.height}` }
        : { width: `min(${isEmbed ? '80vw' : '85vw'}, 854px)`, aspectRatio: '16 / 9' };

    const renderElement = (element, itemKey) => {
        if (element?.element_type_name?.toLowerCase() == 'image') {
            const configuredWidth = dimensions?.width ?? element?.file_details?.dimensions?.width;
            if (!element?.file_url && isExternalUrl(element?.value?.gif_link) && !shouldShowEmbeddedContent()) {
                return (
                    <CookieBlockedMessage
                        key={itemKey}
                        style={lightbox
                            ? { width: '100%', height: '100%' }
                            : { height: `${element?.geometry?.height ?? dimensions?.height}px`, width: `${element?.geometry?.width ?? dimensions?.width}px` }
                        }
                        dimensions={dimensions}
                        contentType="image"
                    />
                );
            }
            return (
                <div>
                <img
                    key={itemKey}
                    src={element?.file_url ?? element?.value?.gif_link}
                    alt=""
                    style={lightboxSize ? {
                        width: `${lightboxSize.width}px`,
                        height: `${lightboxSize.height}px`,
                        maxWidth: '95vw',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                        display: 'block',
                    } : {
                        width: 'auto',
                        height: 'auto',
                        maxWidth: lightbox
                            ? (configuredWidth ? `min(${configuredWidth}px, 95vw)` : '95vw')
                            : (configuredWidth ? `${configuredWidth}px` : '100%'),
                        maxHeight: lightbox ? '90vh' : undefined,
                        display: 'block',
                    }}
                />
                </div>
            );
        } else if (element?.element_type_name?.toLowerCase() == 'video' || element?.element_type_name?.toLowerCase() === "web url" || element?.element_type_name?.toLowerCase() === "audio") {
            // Check if embedded content should be shown based on cookie consent
            // Cookie blocking only applies in viewer mode (LaserLink flow where cookie modal is shown)
            const canShowEmbedded = shouldShowEmbeddedContent();

            if (!canShowEmbedded && lightbox) {
                return (
                    <div key={itemKey} style={frameStyle}>
                        <CookieBlockedMessage
                            style={{ width: '100%', height: '100%' }}
                            dimensions={dimensions}
                            contentType={detectContentType(element)}
                            large
                        />
                    </div>
                );
            }

            if (!canShowEmbedded) {
                return (
                    <CookieBlockedMessage
                        key={itemKey}
                        style={lightbox
                            ? { width: '100%', height: '100%' }
                            : { height: `${element?.geometry?.height ?? dimensions?.height}px`, width: `${element?.geometry?.width ?? dimensions?.width}px` }
                        }
                        dimensions={dimensions}
                        contentType={detectContentType(element)}
                    />
                );
            }

            // SIMPLE FIX: If video_link contains <script> tag, inject HTML directly (ThriveCart, etc.)
            if (/<script/i.test(element?.value?.video_link)) {
                return (
                    <ScriptEmbed
                        key={itemKey}
                        htmlContent={element.value.video_link}
                        height={lightbox ? '100%' : (element?.geometry?.height ?? dimensions?.height)}
                        width={lightbox ? '100%' : (element?.geometry?.width ?? dimensions?.width)}
                    />
                );
            }

            const isTrackedVideo = videoMonitor && element?.element_type_name?.toLowerCase() === 'video'
                && ['youtube', 'vimeo'].includes(element?.value?.source);
            if (lightbox && isTrackedVideo) {
                const Player = element.value.source === 'vimeo' ? VimeoIframe : YoutubeIframe;
                return (
                    <div key={itemKey} style={frameStyle}>
                        <Player
                            src={element?.value?.video_link}
                            id={element?.id}
                            title="url"
                            actionCableVideoMontior={videoMonitor}
                            editortype="laserlink"
                            allowInteraction
                            style={{ width: '100%', height: '100%' }}
                        />
                    </div>
                );
            }

            // In lightbox, wrap in a responsive 16:9 container so video fills naturally.
            if (lightbox) {
                return (
                    <div key={itemKey} style={frameStyle}>
                        <SafeIFrame
                            style={{ width: '100%', height: '100%' }}
                            scrolling="yes"
                            src={element?.value?.video_link}
                            title="url"
                        />
                    </div>
                );
            }

            // Standard iframe embed (YouTube, Vimeo, etc.)
            return (
                <SafeIFrame
                    key={itemKey}
                    style={{
                        height: `${element?.geometry?.height ?? dimensions?.height}px`,
                        width: `${element?.geometry?.width ?? dimensions?.width}px`,
                    }}
                    scrolling="yes"
                    src={element?.value?.video_link}
                    title="url"
                />
            );
        } else if (element?.element_type_name?.toLowerCase() == 'html text') {
            const safeHtml = sanitizeHtml(element.value.html_text);
            const lightboxHtml = shouldShowEmbeddedContent() ? safeHtml : gateThirdPartyHtml(safeHtml);
            return (
                <div
                    className='bg-white'
                    style={{ width: `${dimensions?.width}px`, height: `${dimensions?.height}px`, ...(lightbox && { maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto' }) }}
                    onClick={(event) => {
                        if (!event.target.closest('[data-kz-cookie-settings]')) return;
                        event.stopPropagation();
                        window.dispatchEvent(new CustomEvent('openCookieConsentModal'));
                    }}
                    dangerouslySetInnerHTML={{ __html: lightboxHtml }}
                />
            );
        }
        return "Not an Element";
    }
    return (
        <>
            {
                Object.keys(elements)?.map((elementKey, itemKey) => <div key={itemKey}>{renderElement(elements[elementKey], itemKey)}</div>)
            }
        </>
    )
}
export default Element;